import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine, ComposedChart, Area,
} from 'recharts';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';

const RANGES = [
  { key: '1W', label: '1W', days: 7 },
  { key: '1M', label: '1M', days: 30 },
  { key: '3M', label: '3M', days: 90 },
  { key: '6M', label: '6M', days: 180 },
  { key: '1Y', label: '1Y', days: 365 },
  { key: 'ALL', label: 'All', days: null },
];

// Gold primary, deep-gold min, green moving average. No cyan/blue.
const COLORS = {
  avg: '#FFB800',
  min: '#E09E00',
  movingAvg: '#4FB98A',
  range: '#FFC21E',
};

const SpO2Chart = () => {
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('1M');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiClient.get('/api/fitbit/spo2');
        if (response.status === 200) setAllData(response.data);
      } catch (error) {
        toast.error('Failed to load SpO2 data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredData = useMemo(() => {
    if (!allData.length) return [];
    const rangeConfig = RANGES.find(r => r.key === range);
    if (!rangeConfig?.days) return allData;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - rangeConfig.days);
    const cutoffStr = cutoff.toISOString().split('T')[0];
    return allData.filter(d => d.date >= cutoffStr);
  }, [allData, range]);

  const stats = useMemo(() => {
    if (!filteredData.length) return null;
    const avgVals = filteredData.filter(d => d.avg_spo2 != null).map(d => d.avg_spo2);
    const minVals = filteredData.filter(d => d.min_spo2 != null).map(d => d.min_spo2);
    const latest = filteredData[filteredData.length - 1];
    return {
      latestAvg: latest.avg_spo2,
      latestMin: latest.min_spo2,
      latestMax: latest.max_spo2,
      overallAvg: avgVals.length ? +(avgVals.reduce((a, b) => a + b, 0) / avgVals.length).toFixed(1) : null,
      overallMin: minVals.length ? Math.min(...minVals) : null,
      lowestAvg: avgVals.length ? Math.min(...avgVals) : null,
      totalEntries: filteredData.length,
    };
  }, [filteredData]);

  const chartData = useMemo(() => {
    if (!filteredData.length) return [];
    const windowSize = filteredData.length > 90 ? 14 : 7;

    return filteredData.map((d, i) => {
      const window = filteredData.slice(Math.max(0, i - windowSize + 1), i + 1);
      const avgVals = window.filter(w => w.avg_spo2 != null).map(w => w.avg_spo2);
      const movingAvg = avgVals.length >= 2
        ? +(avgVals.reduce((a, b) => a + b, 0) / avgVals.length).toFixed(1)
        : null;

      return {
        date: d.date,
        avg: d.avg_spo2,
        min: d.min_spo2,
        max: d.max_spo2,
        movingAvg,
      };
    });
  }, [filteredData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!allData.length) {
    return (
      <Card>
        <CardContent className="py-12">
          <p className="text-center text-gray-500 dark:text-gray-400">
            No SpO2 data available. This requires a Fitbit device with SpO2 tracking capability.
          </p>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    const rangeConfig = RANGES.find(r => r.key === range);
    const days = rangeConfig?.days;
    if (days && days <= 30) {
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const tickInterval = Math.max(0, Math.ceil(chartData.length / 8) - 1);

  const SpO2Tooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    const dateLabel = new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    });
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg text-sm">
        <p className="font-semibold text-gray-900 dark:text-white mb-2">{dateLabel}</p>
        <div className="space-y-1">
          {d.avg != null && (
            <p style={{ color: COLORS.avg }}>
              Average: <span className="font-semibold">{d.avg}%</span>
            </p>
          )}
          {d.min != null && (
            <p style={{ color: COLORS.min }}>
              Min: <span className="font-semibold">{d.min}%</span>
            </p>
          )}
          {d.max != null && (
            <p className="text-gray-500 dark:text-gray-400">
              Max: <span className="font-semibold">{d.max}%</span>
            </p>
          )}
          {d.movingAvg != null && (
            <p style={{ color: COLORS.movingAvg }}>
              Moving Avg: <span className="font-semibold">{d.movingAvg}%</span>
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Range Selector */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex flex-wrap gap-1 bg-gray-100 dark:bg-surface-dark-raised rounded-lg p-1">
          {RANGES.map(r => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                range === r.key
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {stats?.totalEntries} nights
        </span>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Latest Avg</p>
            <p className="text-2xl font-bold" style={{ color: COLORS.avg }}>
              {stats?.latestAvg != null ? `${stats.latestAvg}%` : '--'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Period Avg</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats?.overallAvg != null ? `${stats.overallAvg}%` : '--'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Lowest Avg</p>
            <p className="text-2xl font-bold" style={{ color: COLORS.min }}>
              {stats?.lowestAvg != null ? `${stats.lowestAvg}%` : '--'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Lowest Min</p>
            <p className="text-2xl font-bold" style={{ color: COLORS.min }}>
              {stats?.overallMin != null ? `${stats.overallMin}%` : '--'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* SpO2 Trend Chart */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Blood Oxygen (SpO2)</CardTitle>
            <span className="text-xs text-gray-400">Normal: 95–100%</span>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#6b7280" opacity={0.15} />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                interval={tickInterval}
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                axisLine={{ stroke: '#4b5563' }}
                tickLine={{ stroke: '#4b5563' }}
              />
              <YAxis
                domain={[
                  (dataMin) => Math.floor(Math.min(dataMin, 90)),
                  100,
                ]}
                tickFormatter={(v) => `${v}%`}
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                axisLine={{ stroke: '#4b5563' }}
                tickLine={{ stroke: '#4b5563' }}
                width={48}
              />
              <Tooltip content={<SpO2Tooltip />} />
              <Legend
                formatter={(value) => {
                  const map = { avg: 'Average', min: 'Nightly Min', movingAvg: 'Moving Avg' };
                  return map[value] || value;
                }}
                wrapperStyle={{ fontSize: 12 }}
              />
              <ReferenceLine
                y={95}
                stroke="#22c55e"
                strokeDasharray="3 3"
                label={{ value: '95%', position: 'insideTopLeft', fill: '#22c55e', fontSize: 11 }}
              />
              <Area
                dataKey="avg"
                stroke={COLORS.avg}
                strokeWidth={2}
                fill={COLORS.avg}
                fillOpacity={0.06}
                type="monotone"
                dot={chartData.length <= 60 ? { r: 3, fill: COLORS.avg } : false}
                connectNulls
                name="avg"
              />
              <Line
                dataKey="min"
                stroke={COLORS.min}
                strokeWidth={1.5}
                dot={chartData.length <= 60 ? { r: 2, fill: COLORS.min } : false}
                type="monotone"
                connectNulls
                name="min"
                strokeDasharray="2 2"
              />
              <Line
                dataKey="movingAvg"
                stroke={COLORS.movingAvg}
                strokeWidth={2}
                dot={false}
                name="movingAvg"
                type="monotone"
                connectNulls
                strokeDasharray="5 3"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Min/Max Range Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Nightly SpO2 Range</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#6b7280" opacity={0.15} />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                interval={tickInterval}
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                axisLine={{ stroke: '#4b5563' }}
                tickLine={{ stroke: '#4b5563' }}
              />
              <YAxis
                domain={[
                  (dataMin) => Math.floor(Math.min(dataMin, 88)),
                  100,
                ]}
                tickFormatter={(v) => `${v}%`}
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                axisLine={{ stroke: '#4b5563' }}
                tickLine={{ stroke: '#4b5563' }}
                width={48}
              />
              <Tooltip content={<SpO2Tooltip />} />
              <Area
                dataKey="max"
                stroke="transparent"
                fill={COLORS.range}
                fillOpacity={0.12}
                type="monotone"
                connectNulls
                name="max"
              />
              <Area
                dataKey="min"
                stroke="transparent"
                fill="#ffffff"
                fillOpacity={1}
                type="monotone"
                connectNulls
                name="minArea"
                className="dark:fill-gray-900"
              />
              <Line
                dataKey="max"
                stroke={COLORS.avg}
                strokeWidth={1.5}
                dot={false}
                type="monotone"
                connectNulls
                name="maxLine"
              />
              <Line
                dataKey="min"
                stroke={COLORS.min}
                strokeWidth={1.5}
                dot={false}
                type="monotone"
                connectNulls
                name="minLine"
              />
              <ReferenceLine y={95} stroke="#22c55e" strokeDasharray="3 3" />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default SpO2Chart;
